import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Check, Lock, Award, ChevronRight, GraduationCap } from "lucide-react";
import Navigation from "../components/Navigation";
import SpeakerButton from "../components/SpeakerButton";
import BadgeDisplay from "../components/BadgeDisplay";
import QuizModal from "../components/QuizModal";
import { fetchCurriculum, completeLesson, fetchCadet } from "../lib/api";

const CREST_BG = "https://static.prod-images.emergentagent.com/jobs/476fc817-b76a-4f2c-9d9d-7d25b61f6972/images/b1eee161d34c19df351979fc5e3123d8fa732372fb8027c7023a52221f9ecc07.png";

export const Academy = () => {
  const [data, setData] = useState(null);
  const [cadet, setCadet] = useState(null);
  const [openModule, setOpenModule] = useState(null); // module id
  const [openLesson, setOpenLesson] = useState(null); // {module_id, lesson_id}
  const [quizModule, setQuizModule] = useState(null);

  const refresh = async () => setData(await fetchCurriculum());

  useEffect(() => {
    refresh();
    fetchCadet().then(setCadet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompleteLesson = async (moduleId, lessonId) => {
    const r = await completeLesson(moduleId, lessonId);
    if (r.cadet) setCadet(r.cadet);
    await refresh();
  };

  if (!data) {
    return (
      <div className="min-h-screen eigoo-stage-bg">
        <Navigation cadet={cadet} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center text-white/60">Loading syllabus...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen eigoo-stage-bg relative overflow-x-hidden">
      <div
        className="absolute inset-0 opacity-15 pointer-events-none"
        style={{ backgroundImage: `url(${CREST_BG})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black pointer-events-none" />

      <Navigation cadet={cadet} />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
        {/* Ceremonial crest header */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
          data-testid="academy-crest"
        >
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full border border-yellow-300/40 bg-yellow-300/5 text-yellow-300 font-display font-bold tracking-[0.35em] text-[10px] md:text-xs mb-5">
            <GraduationCap size={14} /> EST. 2026 · SOVEREIGN FLEET · CYBERKIDS ACADEMY
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95]">
            <span className="bg-gradient-to-b from-yellow-100 via-yellow-300 to-yellow-600 bg-clip-text text-transparent">Signal Science</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-4">
            <p className="italic text-white/70 text-base md:text-lg">"{data.motto}"</p>
            <SpeakerButton text={`Welcome to Signal Science. ${data.motto}`} testid="speaker-academy-motto" size="sm" />
          </div>
          <div className="mt-5 flex items-center justify-center">
            <div className="h-px w-16 bg-yellow-300/40" />
            <div className="mx-3 text-yellow-300 text-sm">✦</div>
            <div className="h-px w-16 bg-yellow-300/40" />
          </div>
        </motion.section>

        {/* Badges earned */}
        <section className="mb-12" data-testid="badges-section">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Award className="text-yellow-300" size={22} />
              <h2 className="font-display font-black text-2xl md:text-3xl">Proof of Skill · The Hall</h2>
              <SpeakerButton text={`You have earned ${data.badges.length} Proof of Skill badges out of four possible.`} testid="speaker-hall" size="sm" />
            </div>
            <div className="text-xs uppercase tracking-widest text-white/50 font-display">{data.badges.length} / {data.modules.length}</div>
          </div>
          <div className="eigoo-glass rounded-3xl p-6 md:p-8">
            <div className="flex items-center justify-center gap-6 md:gap-10 flex-wrap">
              {data.modules.map((m) => {
                const earnedBadge = data.badges.find(b => b.module_id === m.id);
                return (
                  <div key={m.id} className="flex flex-col items-center gap-2" data-testid={`hall-badge-${m.id}`}>
                    <BadgeDisplay
                      badge={{ ...m, module_id: m.id, module_numeral: m.numeral }}
                      size="lg"
                      earned={!!earnedBadge}
                    />
                    <div className="text-center mt-3 max-w-[160px]">
                      <div className="font-display font-bold text-sm leading-tight">{m.title.split(" & ")[0]}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                        {earnedBadge ? <span className="text-green-300">Earned</span> : <span className="flex items-center justify-center gap-1"><Lock size={10}/>Sealed</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modules */}
        <section className="space-y-5" data-testid="modules-section">
          {data.modules.map((m, idx) => {
            const expanded = openModule === m.id;
            const progress = m.progress;
            return (
              <motion.article
                key={m.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="eigoo-glass rounded-3xl overflow-hidden"
                data-testid={`module-${m.id}`}
              >
                <button
                  onClick={() => setOpenModule(expanded ? null : m.id)}
                  className="w-full text-left px-6 md:px-8 py-6 flex items-center gap-5 flex-wrap md:flex-nowrap hover:bg-white/[0.02] transition-colors"
                  data-testid={`module-toggle-${m.id}`}
                >
                  <div className="flex items-center justify-center h-20 w-20 shrink-0 rounded-2xl border-2 border-yellow-300/30 bg-black/40 relative">
                    <div className="font-display font-black text-3xl text-yellow-300">{m.numeral}</div>
                    {m.badge_earned && (
                      <div className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center border-2 border-black">
                        <Check size={14} className="text-black" strokeWidth={3.5} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.25em] text-yellow-300/80 font-display">Module {m.numeral}</div>
                    <div className="font-display font-black text-2xl md:text-3xl leading-tight">{m.title}</div>
                    <div className="text-white/70 mt-1 text-sm md:text-base">{m.summary}</div>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[140px] max-w-[300px]">
                        <div className="h-2 rounded-full bg-black/60 overflow-hidden border border-yellow-300/10">
                          <div className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300" style={{ width: `${progress.pct}%` }} />
                        </div>
                      </div>
                      <div className="text-xs text-white/60">{progress.done}/{progress.total} lessons</div>
                      {m.badge_earned && <span className="text-xs text-green-300 font-display font-bold">✓ Proof of Skill</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <SpeakerButton text={`Module ${m.numeral}: ${m.title}. ${m.summary}`} testid={`speaker-module-${m.id}`} size="sm" />
                    <ChevronRight size={22} className={`text-yellow-300 transition-transform ${expanded ? "rotate-90" : ""}`} />
                  </div>
                </button>

                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden border-t border-yellow-300/10"
                    >
                      <div className="px-6 md:px-8 py-6 space-y-4">
                        {m.lessons.map((ls, li) => {
                          const isOpen = openLesson?.module_id === m.id && openLesson?.lesson_id === ls.id;
                          // A lesson is considered read if it counts toward m.progress.done
                          // We can infer by checking order: first (progress.done) lessons are read (since our backend marks by key, approximation is fine)
                          const readApprox = li < progress.done;
                          return (
                            <div key={ls.id} className="rounded-2xl bg-black/30 border border-yellow-300/10" data-testid={`lesson-${m.id}-${ls.id}`}>
                              <button
                                onClick={() => setOpenLesson(isOpen ? null : { module_id: m.id, lesson_id: ls.id })}
                                className="w-full text-left px-5 py-4 flex items-center gap-3"
                              >
                                <div className={`h-9 w-9 rounded-xl flex items-center justify-center border ${readApprox ? "bg-green-500/15 border-green-500/40 text-green-300" : "bg-white/5 border-white/10 text-yellow-300"}`}>
                                  {readApprox ? <Check size={16} /> : <BookOpen size={16} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-display">Lesson {li + 1}</div>
                                  <div className="font-display font-bold text-lg leading-tight">{ls.title}</div>
                                </div>
                                <SpeakerButton text={`Lesson ${li+1}. ${ls.title}. ${ls.body}`} testid={`speaker-lesson-${m.id}-${ls.id}`} size="sm" />
                                <ChevronRight size={18} className={`text-white/40 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                              </button>
                              <AnimatePresence>
                                {isOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="px-5 pb-5 space-y-3">
                                      <p className="text-white/90 text-base md:text-lg leading-relaxed">{ls.body}</p>
                                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-300/10 border border-yellow-300/30 text-yellow-100 text-sm">
                                        <Award size={14} className="text-yellow-300" />
                                        <span className="font-display font-bold">Power Word: {ls.power_word}</span>
                                        <span className="italic text-white/60">— {ls.power_meaning}</span>
                                      </div>
                                      <div>
                                        <button
                                          onClick={() => handleCompleteLesson(m.id, ls.id)}
                                          className="btn-eigoo btn-gold"
                                          disabled={readApprox}
                                          data-testid={`mark-read-${m.id}-${ls.id}`}
                                        >
                                          <Check size={18} /> {readApprox ? "Read" : "Mark as Read · +15 XP"}
                                        </button>
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}

                        {/* Quiz call-to-action */}
                        <div className="rounded-2xl bg-gradient-to-br from-yellow-300/10 to-red-500/10 border border-yellow-300/30 p-5 flex items-center gap-4 flex-wrap">
                          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center eigoo-glow-gold shrink-0">
                            <Award className="text-black" size={22} />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <div className="font-display font-bold text-lg">Proof of Skill Quiz</div>
                            <div className="text-white/70 text-sm">Pass with 70% or higher to earn the Module {m.numeral} badge, 120 XP, and 50 coins.</div>
                          </div>
                          <button
                            onClick={() => setQuizModule(m)}
                            className="btn-eigoo btn-gold"
                            data-testid={`start-quiz-${m.id}`}
                          >
                            <Award size={18} /> {m.badge_earned ? "Retake Quiz" : "Start Quiz"}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            );
          })}
        </section>

        <footer className="text-center text-white/30 text-[10px] uppercase tracking-[0.35em] font-display py-12 mt-8">
          Sovereign Fleet · Signal Science Curriculum · MMXXVI
        </footer>
      </div>

      <QuizModal
        module={quizModule}
        open={!!quizModule}
        onClose={() => { setQuizModule(null); refresh(); fetchCadet().then(setCadet); }}
        onAwarded={() => { refresh(); }}
      />
    </div>
  );
};

export default Academy;
